import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate, useParams } from 'react-router';
import type { Kolom } from '@xxtract/tabelfilters';
import { Button, Card, CardContent, CardHeader, CardTitle, TreeTable, type TreeNode, type TreeTableColumn } from '@xxtract/ui';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { ariaSort, Filterbalk, SorteerbareKop, useServerTabelData, useTabelStand, type ServerAntwoord } from '@/components/tabel';
import { useStepItems } from '../hooks/useStepItems';
import { useProcessenFilter } from '../hooks/useProcessenFilter';
import { FilterRejectedMessage, isFilterRejected } from '@/components/feedback/FilterRejectedMessage';
import { PROCESS_FILTER_TEKSTEN, ProcessFilters, processFilterTestId } from '../components/ProcessFilters';
import { formatFetchedAt, formatWorkdays, stepLabel } from '../format';
import { CANONICAL_PROCESS_NAMES } from '../processes.constants';
import { PROCESSEN_PAGINAFILTER, procesFilterVelden, STAP_ITEMS_FYSIEK_LIJST, STAP_ITEMS_LIJST, STAP_ITEMS_PAGINAGROOTTE, stapItemsSleutel } from '../procesLijsten';
import type { CanonicalProcessName, StepItemRow } from '../processes.api';

/** Het fysieke proces: stap-items extern gepagineerd (CAP-10). */
const FYSIEK: CanonicalProcessName = 'physical';

function isCanonicalProcessName(value: string | undefined): value is CanonicalProcessName {
  return value != null && (CANONICAL_PROCESS_NAMES as readonly string[]).includes(value);
}

/** Kolombreedtes: kop en cellen delen ze (TreeTable), in px zodat het raster vast ligt. */
const BREEDTE: Record<string, string> = {
  product: 'w-[150px] shrink-0',
  omschrijving: 'w-[200px] shrink-0',
  order: 'w-[110px] shrink-0',
  klant: 'w-[130px] shrink-0',
  dienst: 'w-[90px] shrink-0',
  sinds: 'w-[110px] shrink-0',
  werkdagen: 'w-[130px] shrink-0',
  normstatus: 'w-[110px] shrink-0',
  voortgang: 'w-[70px] shrink-0',
};
const VOORTGANG = 'voortgang';
const CEL = 'truncate text-xs text-[var(--foreground-muted)]';

function KpiStateLabel({ item }: { item: StepItemRow }) {
  const { t } = useTranslation();
  if (item.kpiState === 'not_started') {
    return <span className="text-[var(--foreground-muted)]">{t('processes.kpi.state.not_started')}</span>;
  }
  return (
    <span className="flex items-center gap-1">
      <span>{item.workdaysSinceReceipt != null ? formatWorkdays(item.workdaysSinceReceipt) : t('common.notAvailable')}</span>
      {item.stagnant ? <span className="rounded-full bg-[var(--warning-light)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--warning-text)]" data-testid="step-item-stagnant-badge">{t('processes.knelpunt.stagnantShort')}</span> : null}
    </span>
  );
}

function itemCells(item: StepItemRow, locale: string, t: TFunction): Record<string, ReactNode> {
  return {
    product: <span className="truncate font-medium text-[var(--foreground)]">{item.gtin}</span>,
    omschrijving: <span className="truncate text-xs text-[var(--foreground)]">{item.description ?? t('common.notAvailable')}</span>,
    order: <span className={CEL}>{item.orderNumber}</span>,
    klant: <span className={CEL}>{item.gln ?? t('common.notAvailable')}</span>,
    dienst: <span className={CEL}>{item.service}</span>,
    sinds: <span className={CEL}>{item.receivedAt != null ? formatFetchedAt(item.receivedAt, locale) : t('processes.kpi.state.not_started')}</span>,
    // De werkdagcel draagt de rij-kenmerken: de normstatus zonder kleur af te lezen (Story 3.4).
    werkdagen: <span className="min-w-0 truncate text-xs" data-kpi-state={item.kpiState} data-testid="step-detail-row"><KpiStateLabel item={item} /></span>,
    normstatus: <span className={CEL}>{t(`processes.kpi.state.${item.kpiState}`)}</span>,
    [VOORTGANG]: <span className={CEL}>{t('processes.stepDetail.progressValue', { x: item.progress.x, y: item.progress.y })}</span>,
  };
}

/**
 * Stap-items van een proces (spec-tabelfilters story 8): centrale filterbalk, sorteerbare koppen binnen
 * de boomtabel en de lijststand `stapItems:<name>:<step>`. Het dienstpad filtert en sorteert op de
 * server in het geheugen; het fysieke pad is extern gepagineerd en kent alleen één klant (CAP-10).
 */
export function StapItems() {
  const { t, i18n } = useTranslation();
  const kopRef = useRef<HTMLHeadingElement>(null);
  const kaartTitelId = useId();
  const navigate = useNavigate();
  const params = useParams<{ name: string; step: string }>();
  const name = isCanonicalProcessName(params.name) ? params.name : null;
  const step = params.step != null ? decodeURIComponent(params.step) : null;

  const fysiek = name === FYSIEK;
  const lijst = fysiek ? STAP_ITEMS_FYSIEK_LIJST : STAP_ITEMS_LIJST;
  const sleutel = stapItemsSleutel(name ?? '', step ?? '');
  const velden = useMemo(() => procesFilterVelden(lijst), [lijst]);
  const { values: filterValues, setFilters, wis, start, geweigerd, defaultStilstandDrempelWerkdagen } = useProcessenFilter(useMemo(() => [{ lijst, standSleutel: sleutel }], [lijst, sleutel]));
  const stand = useTabelStand(lijst, sleutel, { start: start[sleutel], afhankelijkVan: PROCESSEN_PAGINAFILTER, paginagrootte: STAP_ITEMS_PAGINAGROOTTE });
  const context = useMemo(() => ({ t: (s: string) => t(s) }), [t]);
  const standaard = lijst.standaardSortering?.(context);

  // Het antwoord (totaal, facetten) komt na de vraag binnen en gaat via de stand een render later de hook in.
  // Met de standsleutel erbij: een ander `:step` hergebruikt de component, en dan hoort het oude totaal er niet bij.
  const [antwoord, setAntwoord] = useState<{ sleutel: string; a: ServerAntwoord }>();
  const tabel = useServerTabelData(lijst, stand, antwoord?.sleutel === sleutel ? antwoord.a : undefined, { extern: fysiek });
  const { page: _page, pageSize: _pageSize, ...kolommen } = tabel.parameters;
  const itemsQuery = useStepItems(name, step, filterValues, tabel.pagina, kolommen);
  const antwoordData = itemsQuery.data?.data;
  useEffect(() => {
    if (!antwoordData) return;
    // Het fysieke pad geeft geen totaal: de pager blijft op `hasMore`.
    setAntwoord({ sleutel, a: { totaal: fysiek ? undefined : (antwoordData.total ?? 0), facetten: antwoordData.facetten, suggesties: antwoordData.suggesties, afgekapt: antwoordData.afgekapt } });
  }, [antwoordData, fysiek, sleutel]);

  function openOrderItem(item: StepItemRow) {
    navigate(`/orders/${item.orderNumber}/items/${item.tradeItemId}`);
  }

  if (name == null || step == null) {
    return (
      <p className="text-sm text-[var(--foreground-muted)]" data-testid="step-detail-unknown" role="alert">
        {t('errors.notFound')}
      </p>
    );
  }

  // CAP-2: een geweigerd filter keert niet terug vóór de filterbalk — die is de weg terug.
  const filterRejected = isFilterRejected(itemsQuery.error, velden, antwoordData?.sources.procesManagement);
  if (itemsQuery.isError && !filterRejected) {
    return <ApiErrorMessage error={itemsQuery.error} onRetry={() => itemsQuery.refetch()} />;
  }

  const data = filterRejected ? undefined : antwoordData;
  const processLabel = t(`processes.names.${name}`);
  const bronOnbereikbaar = data?.sources.procesManagement === 'unavailable';
  const kolomfilterActief = Object.keys(stand.filters).length > 0;
  // Voorrang (spec): onbereikbare bron en niet-gedekte stap winnen; "geen treffers" alleen door kolomfilters.
  const geenTreffers = !fysiek && !bronOnbereikbaar && kolomfilterActief && (data?.total ?? 0) === 0;

  const columns: TreeTableColumn[] = [
    ...lijst.kolommen.map((k) => ({
      id: k.sleutel,
      label: t(k.kopSleutel),
      header: <SorteerbareKop kolom={k as Kolom<never, never>} onKlik={() => stand.klikOpKop(k.sleutel)} sortering={stand.sortering} standaard={standaard} />,
      ariaSort: ariaSort(stand.sortering, k.sleutel),
      className: BREEDTE[k.sleutel]!,
    })),
    { id: VOORTGANG, label: t('processes.stepDetail.columns.progress'), className: BREEDTE[VOORTGANG]! },
  ];
  const nodes: TreeNode[] = (data?.items ?? []).map((item) => ({
    id: item.tradeItemId,
    label: `${item.description ?? item.gtin} ${item.gtin}`,
    cells: itemCells(item, i18n.language, t),
  }));

  return (
    <div className="space-y-5" data-testid="step-detail-page">
      <header>
        <h1 ref={kopRef} tabIndex={-1} className="text-xl font-bold tracking-tight text-[var(--foreground)]">{t('processes.stepDetail.title', { process: processLabel, step: stepLabel(step, t) })}</h1>
        {data ? (
          <p aria-live="polite" className="text-xs text-[var(--foreground-muted)]" data-testid="step-detail-updated-at">
            {t('processes.updatedAt', { time: formatFetchedAt(data.fetchedAt, i18n.language) })}
          </p>
        ) : null}
      </header>

      <ProcessFilters defaultStilstandDrempelWerkdagen={defaultStilstandDrempelWerkdagen} onChange={setFilters} values={filterValues} />

      <FilterRejectedMessage
        baseTestId={processFilterTestId}
        error={itemsQuery.error}
        focusNaWissen={kopRef}
        linkGeweigerd={geweigerd}
        onClear={() => {
          wis();
          stand.reset();
        }}
        rejected={filterRejected}
        teksten={PROCESS_FILTER_TEKSTEN}
        velden={velden}
      />

      {/* Geweigerd filter: geen kaart, maar de filterbalk blijft, zodat het filter aan te passen is. */}
      {filterRejected ? <Filterbalk context={context} data={tabel} lijst={lijst} stand={stand} /> : null}

      {itemsQuery.isLoading ? <div className="h-48 animate-pulse rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background-muted)]" data-testid="step-detail-loading" /> : null}

      {data ? (
        <>
          {bronOnbereikbaar ? (
            <p className="text-xs text-[var(--info-text)]" role="status">{t('processes.degraded.title')}</p>
          ) : null}
          {/* Story 7 (CAP-10): not all orders were fetched, so the list may be incomplete. */}
          {data.ordersTruncated ? (
            <p className="text-[11px] text-[var(--info-text)]" data-testid="step-items-orders-truncated">{t('processes.ordersTruncated.lijst')}</p>
          ) : null}

          {data.coverageState !== 'covered' ? (
            <p className="text-xs text-[var(--foreground-muted)]" data-testid="step-detail-no-data">{t('processes.noData.title')}</p>
          ) : (
            <Card data-testid="step-detail-card">
              <CardHeader>
                <CardTitle id={kaartTitelId}>{t('processes.stepDetail.summaryTitle')}</CardTitle>
                {/* De samenvatting telt zonder kolomfilters ("n van m"); het aantal treffers staat bij de pager. */}
                <p className="text-xs text-[var(--foreground-muted)]" data-testid="step-detail-summary">
                  {t('processes.stepDetail.summary', {
                    productCount: data.summary.productCount,
                    orderCount: data.summary.orderCount,
                    meanWorkdays: data.summary.meanWorkdays != null ? formatWorkdays(data.summary.meanWorkdays) : t('common.notAvailable'),
                    pctWithinKpi: data.summary.pctWithinKpi != null ? Math.round(data.summary.pctWithinKpi) : t('common.notAvailable'),
                    overKpiCount: data.summary.overKpiCount,
                    notReceivedCount: data.summary.notReceivedCount,
                  })}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Het fysieke pad kent geen totaal: de balk kondigt dan de getoonde regels aan. */}
                <Filterbalk context={context} data={fysiek ? { ...tabel, totaal: data.items.length } : tabel} lijst={lijst} stand={stand} />

                {nodes.length === 0 ? (
                  <p className="text-xs text-[var(--foreground-muted)]" data-testid="step-detail-empty">{t(geenTreffers ? 'tabel.geenTreffers' : 'processes.stepDetail.empty')}</p>
                ) : (
                  // Kop en rijen samen in één scrollvak (de kop staat binnen de boomtabel, CAP-12): op 375 px
                  // "Extra groot" schuift de tabel in haar eigen vak en niet de pagina.
                  // Focusbaar (naam = kaarttitel), omdat de rijen pijl-links/rechts zelf afvangen.
                  // Omlijning met ! (niet ring): de globale *:focus-visible-regel in globals.css wint anders van elke
                  // ring-utility, en een omlijning tekent boven de koprij-achtergrond uit.
                  <div aria-labelledby={kaartTitelId} className="overflow-x-auto focus-visible:outline-2! focus-visible:-outline-offset-2! focus-visible:outline-[var(--secondary)]!" role="region" tabIndex={0}>
                    <div className="min-w-max">
                      <TreeTable
                        ariaLabel={t('processes.stepDetail.summaryTitle')}
                        className="overflow-x-visible"
                        columns={columns}
                        data-testid="step-detail-tree"
                        nodes={nodes}
                        onOpen={(node) => {
                          const item = data.items.find((candidate) => candidate.tradeItemId === node.id);
                          if (item != null) openOrderItem(item);
                        }}
                        onSelectedIdChange={(id) => {
                          const item = data.items.find((candidate) => candidate.tradeItemId === id);
                          if (item != null) openOrderItem(item);
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Button data-testid="step-detail-prev-page" disabled={tabel.pagina <= 1} onClick={() => stand.zetPagina(tabel.pagina - 1)} size="sm" variant="secondary">
                    {t('processes.stepDetail.previousPage')}
                  </Button>
                  <span className="text-xs text-[var(--foreground-muted)]" data-testid="step-detail-page-indicator">
                    {t('processes.stepDetail.pageIndicator', { page: tabel.pagina })}
                    {fysiek ? null : <span className="ml-2" data-testid="step-detail-treffers">{t('tabel.treffers', { count: data.total ?? 0 })}</span>}
                  </span>
                  <Button data-testid="step-detail-next-page" disabled={!data.hasMore} onClick={() => stand.zetPagina(tabel.pagina + 1)} size="sm" variant="secondary">
                    {t('processes.stepDetail.nextPage')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : null}
    </div>
  );
}
